import {
  ENTRY_FEE_EPSON_S5,
  FIRST_PLACE_PRIZE_EPSON_S5,
  INITIAL_PURSE_EPSON_S5,
  MIN_PRIZE_EPSON_S5,
  PRIZE_SCALING_EPSON_S5,
  SIDE_GAMES_EPSON_S5,
  SPONSOR_PURSE_EPSON_S5,
} from "@/utils/constants";

const paidPlacesFor = (playersCount: number) =>
  Math.max(1, Math.trunc(playersCount * 0.1));

const poolFor = (playersCount: number) =>
  INITIAL_PURSE_EPSON_S5 +
  SPONSOR_PURSE_EPSON_S5 +
  ENTRY_FEE_EPSON_S5 * playersCount -
  SIDE_GAMES_EPSON_S5;

/** Weight of a rank below the winner: decaying scale x reverse rank. */
const weightFor = (rank: number, paidPlaces: number) =>
  Math.max(1, 10 * Math.pow(1 - PRIZE_SCALING_EPSON_S5, rank - 2)) *
  (paidPlaces - rank + 1);

/**
 * LPGA Epson NEXT Qualifier, season 5.
 *
 *   purse    = Trackman + sponsor + players x fee
 *   pool     = purse - side games
 *              (side games = 4 x CTP + Longest Drive = $8,500; no Social Media)
 *   M        = trunc(players / 10) paid places, at least 1
 *   rank 1   = fixed FIRST_PLACE_PRIZE
 *   scale(j) = max(1, 10 x (1 - scaling)^(j - 2))
 *   weight(j)= scale(j) x (M - j + 1)      for j >= 2
 *
 * Ranks 3..M take their weighted share of (pool - first place), floored at
 * MIN_PRIZE and rounded to whole dollars. Second place settles last and
 * absorbs rounding and minimum-prize adjustments, so the table totals the
 * pool exactly. With fewer than two paid places (under 20 players) only the
 * fixed first-place prize is paid (matches the finance sheet).
 *
 * @returns Prize per rank, index 0 = 1st, length playersCount, unpaid = 0.
 */
export const prizeCalculatorEpsonS5 = (playersCount: number): number[] => {
  if (!playersCount || playersCount <= 0) return [];

  const pool = poolFor(playersCount);
  const prizes = new Array<number>(playersCount).fill(0);
  if (pool <= 0) return prizes;

  const paidPlaces = Math.min(playersCount, paidPlacesFor(playersCount));

  const firstPlace = Math.min(FIRST_PLACE_PRIZE_EPSON_S5, pool);
  prizes[0] = firstPlace;

  // Too few players for a curve: only the fixed first-place prize is paid.
  if (paidPlaces < 2) return prizes;

  const remainingPool = pool - firstPlace;

  let totalWeight = 0;
  for (let rank = 2; rank <= paidPlaces; rank++) {
    totalWeight += weightFor(rank, paidPlaces);
  }

  let awarded = 0;
  for (let rank = 3; rank <= paidPlaces; rank++) {
    const prize = Math.round(
      Math.max(
        MIN_PRIZE_EPSON_S5,
        (weightFor(rank, paidPlaces) / totalWeight) * remainingPool,
      ),
    );
    prizes[rank - 1] = prize;
    awarded += prize;
  }

  prizes[1] = Math.max(MIN_PRIZE_EPSON_S5, remainingPool - awarded);

  return prizes;
};
